import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { AlertTriangle, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/data/StatusBadge";
import { api, fmtMoney, fmtNum, monthStart, today } from "@/lib/api";

const PIE = ["hsl(357 80% 49%)", "hsl(0 0% 12%)", "hsl(36 52% 55%)", "hsl(152 45% 36%)", "hsl(210 30% 45%)"];

function Kpi({ label, value, hint, tone, testId, dark }) {
  return (
    <div data-testid={testId}
      className={`p-4 sm:p-5 rounded-lg border transition-transform hover:-translate-y-0.5 ${dark ? "bg-[hsl(var(--sidebar))] text-white border-transparent kgd-grain" : "bg-card"}`}>
      <div className={`text-[10px] uppercase tracking-[0.18em] ${dark ? "text-white/50" : "text-muted-foreground"}`}>{label}</div>
      <div className={`font-mono text-lg sm:text-xl font-bold mt-2 ${tone === "red" ? "text-primary" : ""}`}>{value}</div>
      {hint && <div className={`text-[11px] mt-1 ${dark ? "text-white/40" : "text-muted-foreground"}`}>{hint}</div>}
    </div>
  );
}

function Panel({ title, children, action, className = "" }) {
  return (
    <div className={`bg-card border rounded-lg p-4 sm:p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold uppercase tracking-wider">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

const Empty = ({ text = "Aucune donnée validée sur la période" }) => (
  <div className="h-40 flex items-center justify-center text-xs text-muted-foreground border border-dashed rounded">{text}</div>
);

export default function DashboardPage() {
  const [range, setRange] = useState({ from: monthStart(), to: today() });
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dashboard", { params: { date_from: range.from, date_to: range.to } }).then((r) => setD(r.data)).catch(() => {});
  }, [range]);
  if (!d) return <div className="text-sm text-muted-foreground">Chargement du tableau de bord…</div>;
  const t = d.tfj;
  const c = d.counts;
  return (
    <div className="space-y-6">
      <PageHeader title="Tableau de bord" subtitle="Indicateurs calculés en direct sur les données enregistrées (TFJ importés, opérations validées). Le filtre de dates s'applique aux indicateurs de période.">
        <div className="flex items-center gap-2">
          <Input type="date" className="h-9 w-[150px]" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="dashboard-date-from" />
          <span className="text-xs text-muted-foreground">au</span>
          <Input type="date" className="h-9 w-[150px]" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="dashboard-date-to" />
        </div>
      </PageHeader>

      {d.echeances && d.echeances.en_retard + d.echeances.proches > 0 && (
        <Link to="/charges?tab=echeances" className={`flex items-center gap-3 p-3 rounded-lg border text-sm transition-colors ${d.echeances.en_retard ? "bg-red-50 border-red-200 text-red-900 hover:bg-red-100" : "bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100"}`} data-testid="dashboard-echeances-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            {d.echeances.en_retard > 0 && <b>{d.echeances.en_retard} charge(s) en retard ({fmtMoney(d.echeances.montant_retard)}) </b>}
            {d.echeances.proches > 0 && <span>{d.echeances.proches} échéance(s) dans les {d.echeances.delai} prochains jours</span>}
          </span>
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi dark label="CA du jour" value={fmtMoney(d.ca_jour)} hint={`Aujourd'hui (${today()})`} testId="kpi-card-ca-jour" />
        <Kpi dark label="CA du mois" value={fmtMoney(d.ca_mois)} hint="Depuis le 1er du mois en cours" testId="kpi-card-ca-mois" />
        <Kpi label="Stocks disponibles" value={fmtNum(d.stocks.total)} hint={`Dépôts ${fmtNum(d.stocks.depot)} · Sup. ${fmtNum(d.stocks.superviseur)} · Rev. ${fmtNum(d.stocks.revendeur)}`} testId="kpi-card-stocks" />
        <Kpi label="Opérations en cours" value={fmtNum(d.operations.planifiees + d.operations.en_cours)} hint={`Planifiées ${d.operations.planifiees} · En cours ${d.operations.en_cours} · Réalisées (période) ${d.operations.realisees_periode}`} testId="kpi-card-operations" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="CA de la période" value={fmtMoney(t.ventes)} hint={`${t.n || 0} ligne(s) TPE`} testId="kpi-card-ventes" />
        <Kpi label="Gains payés" value={fmtMoney(t.gains_payes + d.gains_module)} hint={`TFJ ${fmtNum(t.gains_payes)} · Module gains ${fmtNum(d.gains_module)}`} testId="kpi-card-gains" />
        <Kpi label="Commissions calculées" value={fmtMoney(d.commissions_calculees)} hint={`Déduites au TFJ : ${fmtMoney(t.commission)} · À payer : ${fmtMoney(d.commissions_a_payer)}`} testId="kpi-card-commissions" />
        <Kpi label="Charges payées" value={fmtMoney(d.charges)} hint={`Engagées non payées : ${fmtMoney(d.charges_engagees)}`} testId="kpi-card-charges" />
        <Kpi label="Montant attendu" value={fmtMoney(t.net_a_verser)} testId="kpi-card-net" />
        <Kpi label="Encaissé (superviseurs)" value={fmtMoney(t.montant_verse)} hint={`Reste : ${fmtMoney(t.ecart)}`} tone={t.ecart > 0 ? "red" : ""} testId="kpi-card-verse" />
        <Kpi label="Trésorerie totale" value={fmtMoney(d.tresorerie_totale)} hint={`${d.comptes.length} compte(s) actif(s)`} testId="kpi-card-tresorerie" />
        <Kpi label="Créances / Dettes" value={fmtMoney(d.creances)} hint={`Dettes : ${fmtMoney(d.dettes)}`} testId="kpi-card-creances" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[["TPE", c.tpes, "/tpe"], ["TPE actifs", c.tpes_actifs, "/tpe"], ["TPE affectés", c.tpes_affectes, "/tpe"],
          ["Revendeurs actifs", c.revendeurs_actifs, "/revendeurs"], ["Superviseurs actifs", c.superviseurs_actifs, "/superviseurs"],
          ["Journées ouvertes", c.tfj_brouillons, "/tfj"]].map(([l, v, to]) => (
          <Link key={l} to={to} className="group p-3 rounded-lg border bg-card hover:border-primary transition-colors" data-testid={`count-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">{l}<ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100" /></div>
            <div className="font-mono text-2xl font-bold mt-1">{v}</div>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Évolution journalière" className="lg:col-span-2">
          {d.daily.length ? (
            <div className="h-64">
              <ResponsiveContainer>
                <AreaChart data={d.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(30 10% 90%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmtNum(v / 1000) + "k"} width={50} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Area type="monotone" dataKey="ventes" name="CA" stroke="hsl(357 80% 49%)" fill="hsl(357 80% 49% / 0.15)" strokeWidth={2} />
                  <Area type="monotone" dataKey="gains" name="Gains" stroke="hsl(0 0% 12%)" fill="hsl(0 0% 12% / 0.06)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : <Empty />}
        </Panel>
        <Panel title="CA par activité">
          {d.by_produit.length ? (
            <>
              <div className="h-40">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={d.by_produit} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
                      {d.by_produit.map((p, i) => <Cell key={p.name} fill={PIE[i % PIE.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtMoney(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-2">
                {d.by_produit.map((p, i) => (
                  <div key={p.name} className="flex justify-between text-xs"><span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: PIE[i % PIE.length] }} />{p.name}</span><span className="font-mono">{fmtMoney(p.value)}</span></div>
                ))}
              </div>
            </>
          ) : <Empty />}
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Top revendeurs">
          {d.top_revendeurs.length ? d.top_revendeurs.map((r, i) => (
            <div key={r.name} className="flex justify-between items-center py-2 border-b last:border-0 text-sm">
              <span className="truncate"><span className="font-mono text-muted-foreground mr-2">{i + 1}</span>{r.name}</span>
              <span className="font-mono text-xs">{fmtMoney(r.ventes)}</span>
            </div>
          )) : <Empty />}
        </Panel>
        <Panel title="Soldes des comptes" action={<Link to="/tresorerie" className="text-xs text-primary">Voir</Link>}>
          {d.comptes.length ? d.comptes.map((a) => (
            <div key={a.nom} className="flex justify-between py-2 border-b last:border-0 text-sm">
              <span className="truncate">{a.nom}</span>
              <span className={`font-mono text-xs ${a.solde < 0 ? "text-primary" : ""}`}>{fmtMoney(a.solde)}</span>
            </div>
          )) : <Empty text="Aucun compte" />}
        </Panel>
        <Panel title="Alertes">
          <div className="space-y-2 text-sm">
            {d.stock_alerts.map((a) => (
              <div key={a.designation} className="flex gap-2 items-start p-2 rounded bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                <AlertTriangle className="h-4 w-4 shrink-0" />Stock bas : {a.designation} ({fmtNum(a.quantite)} / seuil {fmtNum(a.seuil)})
              </div>
            ))}
            {c.tfj_brouillons > 0 && <div className="p-2 rounded bg-sky-50 border border-sky-200 text-sky-900 text-xs">{c.tfj_brouillons} journée(s) TFJ non clôturée(s)</div>}
            {c.gains_en_attente > 0 && <div className="p-2 rounded bg-sky-50 border border-sky-200 text-sky-900 text-xs">{c.gains_en_attente} gain(s) en attente de paiement</div>}
            {!d.stock_alerts.length && !c.tfj_brouillons && !c.gains_en_attente && <Empty text="Aucune alerte" />}
          </div>
        </Panel>
      </div>
    </div>
  );
}
